import React, { useEffect, useMemo, useState } from 'react';
import { tradingService } from '../../services/tradingService';
import InfusionManagerPanel from './InfusionManagerPanel';
import IncomeAccrualPanel from './IncomeAccrualPanel';

// Portfolio → Infusion Manager tab. One place to manage any account's external capital
// events (infusions / withdrawals). These anchor account-level performance: inception = the
// first inflow, every flow is neutralised in the account TWR, and they are the prerequisite
// for viewing/enabling `pool` capital mode on an account.
const InfusionsPage = () => {
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr('');
    tradingService.listIBKRAccounts()
      .then((r) => {
        if (!alive) return;
        const list = Array.isArray(r.data) ? r.data : (r.data?.results || []);
        setAccounts(list);
        if (list.length) setAccountId((cur) => cur ?? list[0].id);
      })
      .catch(() => alive && setErr('Could not load accounts.'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const selected = useMemo(
    () => accounts.find((a) => a.id === accountId) || null,
    [accounts, accountId]);

  return (
    <div className="max-w-4xl">
      <div className="mb-4">
        <h1 className="text-xl font-serif text-gray-900 dark:text-gray-100">Infusion Manager</h1>
        <p className="text-[11px] uppercase tracking-wider text-gray-500 mt-0.5">
          Capital in / out · anchors account inception · required for pool mode
        </p>
      </div>

      {err && (
        <div className="mb-3 rounded-md border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/30 px-3 py-2 text-xs text-red-600 dark:text-red-400">{err}</div>
      )}

      {/* account picker */}
      <div className="mb-4 flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-0.5">
          <span className="text-[9px] uppercase tracking-wider text-gray-500">Account</span>
          <select
            value={accountId ?? ''}
            onChange={(e) => setAccountId(Number(e.target.value))}
            disabled={loading || !accounts.length}
            className="rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#161b22] px-2.5 py-1.5 text-sm text-gray-900 dark:text-gray-100 min-w-[16rem]"
          >
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}{a.is_model ? ' · Model' : ''} · {a.capital_mode === 'pool' ? 'Pool' : 'Per-deploy'}
              </option>
            ))}
          </select>
        </label>
        {selected && (
          <span className="text-[11px] text-gray-400 pb-1.5">
            Mode: <span className="text-gray-600 dark:text-gray-300">{selected.capital_mode === 'pool' ? 'Pool' : 'Per-deploy'}</span>
            {selected.capital_mode !== 'pool' && ' — add infusions here, then switch to Pool on the Performance tab'}
          </span>
        )}
      </div>

      {loading && <div className="text-xs text-gray-400">Loading accounts…</div>}
      {!loading && !accounts.length && <div className="text-xs text-gray-400">No accounts yet.</div>}
      {!loading && selected && (
        <div className="space-y-4">
          <InfusionManagerPanel accountId={selected.id} accountName={selected.name} />
          <IncomeAccrualPanel accountId={selected.id} accountName={selected.name} />
        </div>
      )}
    </div>
  );
};

export default InfusionsPage;
